<template>
  <div class="system-config-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('systemConfig.title') }}</h2>
        <div class="header-actions">
          <a-space>
            <a-button @click="showBatchUpdateModal">
              <template #icon><EditOutlined /></template>
              {{ $t('systemConfig.batchUpdate') }}
            </a-button>
            <a-button @click="initializeConfigs" :loading="initializing">
              <template #icon><SettingOutlined /></template>
              {{ $t('systemConfig.initDefaultConfig') }}
            </a-button>
          </a-space>
        </div>
      </div>
    </div>

    <!-- 系统状态卡片 -->
    <a-row :gutter="16" class="system-status-cards">
        <a-col :span="6">
          <a-card size="small">
            <template #title>
              <span>{{ $t('systemConfig.systemStatus') }}</span>
              <a-tag 
                :color="systemStatus.maintenance_mode ? 'red' : 'green'" 
                style="margin-left: 8px"
              >
                {{ systemStatus.maintenance_mode ? $t('systemConfig.maintenance') : $t('systemConfig.normal') }}
              </a-tag>
            </template>
            <div class="status-item">
              <div class="status-desc">{{ $t('systemConfig.totalConfigs') }}: {{ systemStatus.total_configs }}</div>
              <div class="status-desc">{{ $t('systemConfig.monitoringStatus') }}: {{ systemStatus.monitoring_enabled ? $t('systemConfig.enabled') : $t('systemConfig.disabled') }}</div>
              <a-button size="small" type="link" @click="showSystemStatusModal">
                {{ $t('systemConfig.manage') }}
              </a-button>
            </div>
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card size="small" :title="$t('systemConfig.hotWordTopCount')">
            <div class="config-item">
              <div class="config-value">{{ systemSettings.hot_word_top_count }}</div>
              <div class="config-desc">{{ $t('systemConfig.currentHotWordLimit') }}</div>
              <a-button size="small" type="link" @click="showTranslationSettingsModal">
                {{ $t('systemConfig.modify') }}
              </a-button>
            </div>
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card size="small" :title="$t('systemConfig.projectRetentionDays')">
            <div class="config-item">
              <div class="config-value">{{ systemSettings.project_retention_days }}</div>
              <div class="config-desc">{{ $t('systemConfig.projectFileRetentionDays') }}</div>
              <a-button size="small" type="link" @click="showCleanupSettingsModal">
                {{ $t('systemConfig.modify') }}
              </a-button>
            </div>
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card size="small" :title="$t('systemConfig.maxFileSize')">
            <div class="config-item">
              <div class="config-value">{{ formatFileSize(systemSettings.max_file_size) }}</div>
              <div class="config-desc">{{ $t('systemConfig.fileUploadSizeLimit') }}</div>
              <a-button size="small" type="link" @click="editConfig('max_file_size')">
                {{ $t('systemConfig.modify') }}
              </a-button>
            </div>
          </a-card>
        </a-col>
    </a-row>

    <!-- L2 Collab 配置表 CRUD -->
    <CollabCrud
      ref="collabCrudRef"
      :service="configService"
      :columns="columns"
      :form-rules="formRules"
      :permission="permission"
      :create-defaults="createDefaults"
      :search-placeholder="$t('systemConfig.searchConfigKeyOrDesc')"
    >
      <template #form="{ mode, model }">
        <a-form-item :label="$t('systemConfig.configKey')" name="config_key">
          <a-input
            v-model:value="model.config_key"
            :placeholder="$t('systemConfig.enterConfigKey')"
            :disabled="mode === 'edit'"
          />
        </a-form-item>
        <a-form-item :label="$t('systemConfig.configType')" name="config_type">
          <a-select v-model:value="model.config_type" :placeholder="$t('systemConfig.selectConfigType')">
            <a-select-option value="string">{{ $t('systemConfig.string') }}</a-select-option>
            <a-select-option value="number">{{ $t('systemConfig.number') }}</a-select-option>
            <a-select-option value="boolean">{{ $t('systemConfig.boolean') }}</a-select-option>
            <a-select-option value="json">{{ $t('systemConfig.json') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('systemConfig.configValue')" name="config_value">
          <a-textarea
            v-if="model.config_type === 'json'"
            v-model:value="model.config_value"
            :placeholder="$t('systemConfig.enterJsonConfigValue')"
            :rows="4"
          />
          <a-select
            v-else-if="model.config_type === 'boolean'"
            v-model:value="model.config_value"
            :placeholder="$t('systemConfig.selectBooleanValue')"
          >
            <a-select-option value="true">{{ $t('systemConfig.yesTrue') }}</a-select-option>
            <a-select-option value="false">{{ $t('systemConfig.noFalse') }}</a-select-option>
          </a-select>
          <a-input-number
            v-else-if="model.config_type === 'number'"
            v-model:value="model.config_value"
            :placeholder="$t('systemConfig.enterNumber')"
            style="width: 100%"
          />
          <a-input
            v-else
            v-model:value="model.config_value"
            :placeholder="$t('systemConfig.enterConfigValue')"
          />
        </a-form-item>
        <a-form-item :label="$t('systemConfig.description')" name="description">
          <a-textarea
            v-model:value="model.description"
            :placeholder="$t('systemConfig.enterDescription')"
            :rows="2"
          />
        </a-form-item>
      </template>

      <template #config_type="{ text }">
        <a-tag :color="getTypeColor(text)">
          {{ getTypeLabel(text) }}
        </a-tag>
      </template>

      <template #config_value="{ record }">
        <div class="config-value-cell">
          <span v-if="record.config_type === 'json'" class="json-value">
            {{ formatJsonValue(record.config_value) }}
          </span>
          <span v-else-if="record.config_type === 'boolean'" class="boolean-value">
            <a-tag :color="record.config_value === 'true' ? 'green' : 'red'">
              {{ record.config_value === 'true' ? $t('systemConfig.yes') : $t('systemConfig.no') }}
            </a-tag>
          </span>
          <span v-else class="text-value">{{ record.config_value }}</span>
        </div>
      </template>

      <template #updated_at="{ text }">
        {{ formatDateTime(text) }}
      </template>
    </CollabCrud>

    <!-- 热词TOP数量配置模态框 -->
    <a-modal
      v-model:open="hotWordTopModalVisible"
      :title="$t('systemConfig.hotWordTopCountConfig')"
      @ok="handleHotWordTopSubmit"
      @cancel="hotWordTopModalVisible = false"
      :confirm-loading="submitting"
    >
      <a-form layout="vertical">
        <a-form-item :label="$t('systemConfig.hotWordTopCount')">
          <a-input-number
            v-model:value="hotWordTopCount"
            :min="1"
            :max="50"
            :placeholder="$t('systemConfig.enterNumberBetween')"
            style="width: 100%"
          />
          <div class="form-help">
            {{ $t('systemConfig.hotWordTopCountHelp') }}
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 项目保留天数配置模态框 -->
    <a-modal
      v-model:open="retentionModalVisible"
      :title="$t('systemConfig.projectRetentionDaysConfig')"
      @ok="handleRetentionSubmit"
      @cancel="retentionModalVisible = false"
      :confirm-loading="submitting"
    >
      <a-form layout="vertical">
        <a-form-item :label="$t('systemConfig.projectRetentionDays')">
          <a-input-number
            v-model:value="retentionDays"
            :min="30"
            :max="365"
            :placeholder="$t('systemConfig.enterDaysBetween')"
            style="width: 100%"
          />
          <div class="form-help">
            {{ $t('systemConfig.retentionDaysHelp') }}
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 批量更新模态框 -->
    <a-modal
      v-model:open="batchUpdateModalVisible"
      :title="$t('systemConfig.batchUpdateConfig')"
      @ok="handleBatchUpdateSubmit"
      @cancel="batchUpdateModalVisible = false"
      :confirm-loading="submitting"
      width="800px"
    >
      <a-form layout="vertical">
        <a-form-item :label="$t('systemConfig.configValue')">
          <a-textarea
            v-model:value="batchUpdateData"
            :placeholder="$t('systemConfig.enterJsonConfigData')"
            :rows="10"
          />
          <div class="form-help">
            {{ $t('systemConfig.batchUpdateHelp') }}
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 系统状态管理模态框 -->
    <a-modal
      v-model:open="systemStatusModalVisible"
      :title="$t('systemConfig.systemStatusManagement')"
      @ok="handleSystemStatusSubmit"
      @cancel="systemStatusModalVisible = false"
      :confirm-loading="submitting"
    >
      <a-form layout="vertical">
        <a-form-item :label="$t('systemConfig.maintenanceMode')">
          <a-switch
            v-model:checked="maintenanceMode"
            :checked-children="$t('systemConfig.enableMaintenance')"
            :un-checked-children="$t('systemConfig.disableMaintenance')"
          />
          <div class="form-help">
            {{ $t('systemConfig.maintenanceModeHelp') }}
          </div>
        </a-form-item>
        <a-form-item :label="$t('systemConfig.systemStatistics')">
          <a-descriptions :column="1" size="small">
            <a-descriptions-item :label="$t('systemConfig.totalConfigs')">
              {{ systemStatus.total_configs }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('systemConfig.configTypeDistribution')">
              <a-space>
                <a-tag v-for="(count, type) in systemStatus.config_types" :key="type">
                  {{ getTypeLabel(type) }}: {{ count }}
                </a-tag>
              </a-space>
            </a-descriptions-item>
          </a-descriptions>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 翻译设置模态框 -->
    <a-modal
      v-model:open="translationSettingsModalVisible"
      :title="$t('systemConfig.translationSettings')"
      @ok="handleTranslationSettingsSubmit"
      @cancel="translationSettingsModalVisible = false"
      :confirm-loading="submitting"
      width="600px"
    >
      <a-form layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('systemConfig.hotWordTopCount')">
              <a-input-number
                v-model:value="translationSettings.hot_word_top_count"
                :min="1"
                :max="50"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('systemConfig.translationTimeout')">
              <a-input-number
                v-model:value="translationSettings.translation_timeout"
                :min="60"
                :max="3600"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('systemConfig.maxConcurrentTranslations')">
              <a-input-number
                v-model:value="translationSettings.max_concurrent_translations"
                :min="1"
                :max="50"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('systemConfig.maxSubtitleLength')">
              <a-input-number
                v-model:value="translationSettings.max_subtitle_length"
                :min="50"
                :max="500"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('systemConfig.defaultSourceLanguage')">
              <a-input v-model:value="translationSettings.default_source_language" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('systemConfig.defaultTargetLanguage')">
              <a-input v-model:value="translationSettings.default_target_language" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item :label="$t('systemConfig.enableTranslationCache')">
          <a-switch
            v-model:checked="translationSettings.enable_translation_cache"
            :checked-children="$t('systemConfig.enableMaintenance')"
            :un-checked-children="$t('systemConfig.disableMaintenance')"
          />
        </a-form-item>
        <a-form-item :label="$t('systemConfig.cacheExpirationTime')" v-if="translationSettings.enable_translation_cache">
          <a-input-number
            v-model:value="translationSettings.translation_cache_ttl"
            :min="3600"
            :max="604800"
            style="width: 100%"
          />
          <div class="form-help">{{ $t('systemConfig.cacheExpirationHelp') }}</div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 清理设置模态框 -->
    <a-modal
      v-model:open="cleanupSettingsModalVisible"
      :title="$t('systemConfig.cleanupSettings')"
      @ok="handleCleanupSettingsSubmit"
      @cancel="cleanupSettingsModalVisible = false"
      :confirm-loading="submitting"
    >
      <a-form layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item :label="$t('systemConfig.projectRetentionDays')">
              <a-input-number
                v-model:value="cleanupSettings.project_retention_days"
                :min="30"
                :max="365"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item :label="$t('systemConfig.cleanupNotificationDays')">
              <a-input-number
                v-model:value="cleanupSettings.cleanup_notification_days"
                :min="1"
                :max="30"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item :label="$t('systemConfig.logRetentionDays')">
          <a-input-number
            v-model:value="cleanupSettings.log_retention_days"
            :min="7"
            :max="365"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item :label="$t('systemConfig.enableAutoCleanup')">
          <a-switch
            v-model:checked="cleanupSettings.enable_auto_cleanup"
            :checked-children="$t('systemConfig.enableMaintenance')"
            :un-checked-children="$t('systemConfig.disableMaintenance')"
          />
          <div class="form-help">
            {{ $t('systemConfig.autoCleanupHelp') }}
          </div>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { EditOutlined, SettingOutlined } from '@ant-design/icons-vue'
import type { FormProps, TableColumnsType } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { CollabCrud, createCollabService } from '@/collab-core'
import { PermissionCode } from '@/constants/permission-codes'
import { formatDateTime, formatFileSize } from '@/shared/utils'
import {
  batchUpdateSystemConfigs,
  getSystemSettingsOverview,
  updateHotWordTopCount,
  updateProjectRetentionDays,
  initializeDefaultConfigs,
  getSystemStatus,
  setMaintenanceMode,
  getCleanupSettings,
  updateCleanupSettings,
  getTranslationSettings,
  updateTranslationSettings,
  type SystemConfig,
  type SystemSettings
} from '../api'
import { runApiBatch, type BatchContext } from '@/shared/api/use-api-batch'

const { t } = useI18n()

/** Collab L2 系统配置服务 */
const configService = createCollabService<SystemConfig>('/system/configs')

const permission = {
  add: PermissionCode.SYSTEM_CONFIG,
  update: PermissionCode.SYSTEM_CONFIG,
  delete: PermissionCode.SYSTEM_CONFIG,
}

const createDefaults = {
  config_key: '',
  config_value: '',
  config_type: 'string',
  description: '',
}

const collabCrudRef = ref<InstanceType<typeof CollabCrud> | null>(null)

const submitting = ref(false)
const initializing = ref(false)

const systemSettings = ref<SystemSettings>({
  hot_word_top_count: 20,
  project_retention_days: 90,
  max_file_size: 2147483648,
  supported_video_formats: [],
  supported_document_formats: [],
  supported_subtitle_formats: []
})

const systemStatus = ref({
  maintenance_mode: false,
  monitoring_enabled: true,
  total_configs: 0,
  config_types: {} as Record<string, number>
})

const hotWordTopModalVisible = ref(false)
const retentionModalVisible = ref(false)
const batchUpdateModalVisible = ref(false)
const systemStatusModalVisible = ref(false)
const translationSettingsModalVisible = ref(false)
const cleanupSettingsModalVisible = ref(false)

const hotWordTopCount = ref(20)
const retentionDays = ref(90)
const batchUpdateData = ref('')
const maintenanceMode = ref(false)

const translationSettings = ref({
  hot_word_top_count: 20,
  translation_timeout: 300,
  max_concurrent_translations: 10,
  max_subtitle_length: 200,
  enable_translation_cache: true,
  translation_cache_ttl: 86400,
  default_source_language: 'zh-cn',
  default_target_language: 'en'
})

const cleanupSettings = ref({
  project_retention_days: 90,
  cleanup_notification_days: 7,
  enable_auto_cleanup: true,
  log_retention_days: 30
})

const formRules = computed<FormProps['rules']>(() => ({
  config_key: [
    { required: true, message: t('systemConfig.configKeyRequired') },
    { pattern: /^[a-zA-Z0-9_.]+$/, message: t('systemConfig.configKeyPattern') }
  ],
  config_type: [
    { required: true, message: t('systemConfig.configTypeRequired') }
  ],
  config_value: [
    { required: true, message: t('systemConfig.configValueRequired') }
  ]
}))

const columns = computed<TableColumnsType>(() => [
  {
    title: t('systemConfig.configKey'),
    dataIndex: 'config_key',
    key: 'config_key',
    width: 200
  },
  {
    title: t('systemConfig.configType'),
    dataIndex: 'config_type',
    key: 'config_type',
    width: 100
  },
  {
    title: t('systemConfig.configValue'),
    dataIndex: 'config_value',
    key: 'config_value',
    width: 300
  },
  {
    title: t('systemConfig.description'),
    dataIndex: 'description',
    key: 'description',
    ellipsis: true
  },
  {
    title: t('systemConfig.updateTime'),
    dataIndex: 'updated_at',
    key: 'updated_at',
    width: 180
  }
])

const getTypeColor = (type: string) => {
  const colors = {
    string: 'blue',
    number: 'green',
    boolean: 'orange',
    json: 'purple'
  }
  return colors[type as keyof typeof colors] || 'default'
}

const getTypeLabel = (type: string) => {
  const labels = {
    string: t('systemConfig.string'),
    number: t('systemConfig.number'),
    boolean: t('systemConfig.boolean'),
    json: t('systemConfig.json')
  }
  return labels[type as keyof typeof labels] || type
}

const formatJsonValue = (value: string) => {
  try {
    const parsed = JSON.parse(value)
    return JSON.stringify(parsed, null, 2).substring(0, 100) + (value.length > 100 ? '...' : '')
  } catch {
    return value
  }
}

/** 刷新 Collab 配置表 */
function refreshCollabList() {
  collabCrudRef.value?.crud.refresh()
}

const loadSystemSettings = async (batchCtx?: BatchContext) => {
  try {
    const settings = await getSystemSettingsOverview(batchCtx?.requestConfig)
    systemSettings.value = settings
  } catch (error: unknown) {
    if (!batchCtx) {
      console.error('Load system settings error:', error)
    }
    throw error
  }
}

const loadSystemStatus = async (batchCtx?: BatchContext) => {
  try {
    const status = await getSystemStatus(batchCtx?.requestConfig)
    systemStatus.value = status
  } catch (error: unknown) {
    if (!batchCtx) {
      console.error('Load system status error:', error)
    }
    throw error
  }
}

const loadTranslationSettings = async () => {
  try {
    const settings = await getTranslationSettings()
    translationSettings.value = settings
  } catch (error) {
    console.error('Load translation settings error:', error)
  }
}

const loadCleanupSettings = async () => {
  try {
    const settings = await getCleanupSettings()
    cleanupSettings.value = settings
  } catch (error) {
    console.error('Load cleanup settings error:', error)
  }
}

const showBatchUpdateModal = () => {
  batchUpdateModalVisible.value = true
  batchUpdateData.value = ''
}

const showSystemStatusModal = async () => {
  await loadSystemStatus()
  maintenanceMode.value = systemStatus.value.maintenance_mode
  systemStatusModalVisible.value = true
}

const showTranslationSettingsModal = async () => {
  await loadTranslationSettings()
  translationSettingsModalVisible.value = true
}

const showCleanupSettingsModal = async () => {
  await loadCleanupSettings()
  cleanupSettingsModalVisible.value = true
}

/** 从状态卡片跳转编辑：按 keyWord 搜索后打开 Collab 编辑表单 */
const editConfig = async (configKey: string) => {
  const crud = collabCrudRef.value?.crud
  if (!crud) {
    message.warning(t('systemConfig.getConfigDetailsFailed'))
    return
  }
  try {
    await crud.handleSearch(configKey)
    const row = crud.dataSource.value.find(
      (item) => (item as SystemConfig).config_key === configKey,
    )
    if (row) {
      crud.rowEdit(row as Record<string, unknown>)
    } else {
      message.warning(t('systemConfig.getConfigDetailsFailed'))
    }
  } catch (error) {
    console.error('Edit config error:', error)
  }
}

const handleHotWordTopSubmit = async () => {
  try {
    submitting.value = true
    await updateHotWordTopCount({ hot_word_top_count: hotWordTopCount.value })
    message.success(t('systemConfig.updateHotWordTopCountSuccess'))
    hotWordTopModalVisible.value = false
    loadSystemSettings()
  } catch (error) {
    console.error('Update hot word top count error:', error)
  } finally {
    submitting.value = false
  }
}

const handleRetentionSubmit = async () => {
  try {
    submitting.value = true
    await updateProjectRetentionDays({ retention_days: retentionDays.value })
    message.success(t('systemConfig.updateRetentionDaysSuccess'))
    retentionModalVisible.value = false
    loadSystemSettings()
  } catch (error) {
    console.error('Update retention days error:', error)
  } finally {
    submitting.value = false
  }
}

const handleBatchUpdateSubmit = async () => {
  try {
    submitting.value = true
    const configs = JSON.parse(batchUpdateData.value)
    await batchUpdateSystemConfigs({ configs })
    message.success(t('systemConfig.batchUpdateSuccess'))
    batchUpdateModalVisible.value = false
    refreshCollabList()
    loadSystemSettings()
  } catch (error) {
    console.error('Batch update configs error:', error)
  } finally {
    submitting.value = false
  }
}

const initializeConfigs = async () => {
  try {
    initializing.value = true
    await initializeDefaultConfigs()
    message.success(t('systemConfig.initDefaultConfigSuccess'))
    refreshCollabList()
    loadSystemSettings()
    loadSystemStatus()
  } catch (error) {
    console.error('Initialize configs error:', error)
  } finally {
    initializing.value = false
  }
}

const handleSystemStatusSubmit = async () => {
  try {
    submitting.value = true
    await setMaintenanceMode(maintenanceMode.value)
    message.success(t('systemConfig.systemStatusUpdateSuccess'))
    systemStatusModalVisible.value = false
    loadSystemStatus()
  } catch (error) {
    console.error('Update system status error:', error)
  } finally {
    submitting.value = false
  }
}

const handleTranslationSettingsSubmit = async () => {
  try {
    submitting.value = true
    await updateTranslationSettings(translationSettings.value)
    message.success(t('systemConfig.translationSettingsUpdateSuccess'))
    translationSettingsModalVisible.value = false
    loadSystemSettings()
  } catch (error) {
    console.error('Update translation settings error:', error)
  } finally {
    submitting.value = false
  }
}

const handleCleanupSettingsSubmit = async () => {
  try {
    submitting.value = true
    await updateCleanupSettings(cleanupSettings.value)
    message.success(t('systemConfig.cleanupSettingsUpdateSuccess'))
    cleanupSettingsModalVisible.value = false
    loadSystemSettings()
  } catch (error) {
    console.error('Update cleanup settings error:', error)
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  runApiBatch([
    (ctx) => loadSystemSettings(ctx),
    (ctx) => loadSystemStatus(ctx),
  ])
})
</script>

<style scoped lang="less">
.system-config-management {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
    
    .header-actions {
      flex-shrink: 0;
    }
  }
}

.system-status-cards {
  margin-bottom: 24px;

  :deep(.ant-card) {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  :deep(.ant-card-body) {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .config-item, .status-item {
    text-align: center;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .config-value {
      font-size: 24px;
      font-weight: bold;
      color: #1890ff;
      margin-bottom: 4px;
    }

    .config-desc, .status-desc {
      color: #666;
      font-size: 12px;
      margin-bottom: 8px;
    }
  }
}

.config-value-cell {
    .json-value {
      font-family: 'Courier New', monospace;
      font-size: 12px;
      color: #722ed1;
    }

    .boolean-value {
      font-weight: bold;
    }

    .text-value {
      word-break: break-all;
    }
}

.form-help {
  color: #666;
  font-size: 12px;
  margin-top: 4px;
}
</style>
